import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { API_CONFIG } from '../services/api/config';

/**
 * R133: announce every access_token store/clear so the live event stream can
 * (re)connect immediately.
 *
 * The /ws/events singleton in hooks/useSecurityEvents loads with the bundle —
 * i.e. while the login screen is showing, with no token — so the gateway 403'd
 * its handshake, it exhausted its retry schedule and fell into a 5-minute idle
 * backoff. Nothing told it a token had arrived, so live updates stayed dead for
 * minutes after sign-in. `storage` events only fire in OTHER tabs, so the tab
 * that actually logged in needs this explicit signal.
 */
function notifyAuthChanged(): void {
  try {
    window.dispatchEvent(new Event('auth:token'));
  } catch { /* non-browser env — nothing to notify */ }
}

// User interface for Cloud Security platform
interface CloudSecurityUser {
  id: number | string;
  email: string;
  username: string;
  full_name?: string;
  role: string;
  is_active: boolean;
  created_at?: string;
  // Cloud security specific fields
  modules?: {
    cspm?: string[];
    cwpp?: string[];
    ciem?: string[];
    dspm?: string[];
    cdr?: string[];
  };
  permissions_summary?: {
    cspm?: { read: boolean; write: boolean; scan: boolean; manage: boolean };
    cwpp?: { read: boolean; write: boolean; monitor: boolean; manage: boolean };
    ciem?: { read: boolean; write: boolean; analyze: boolean; manage: boolean };
    dspm?: { read: boolean; write: boolean; classify: boolean; manage: boolean };
    cdr?: { read: boolean; write: boolean; respond: boolean; manage: boolean };
  };
  scope?: string;
}

// Auth context type
interface AuthContextType {
  user: CloudSecurityUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  getAuthToken: () => string | null;
  hasPermission: (module: string, action: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Hook to check if user is authenticated
export const useIsAuthenticated = () => {
  const { isAuthenticated, loading } = useAuth();
  return { isAuthenticated, loading };
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Start unauthenticated + loading. The app gates on isAuthenticated and
  // shows the login page until a real session is established. (Previously
  // this defaulted to a hardcoded admin, so the login page never appeared.)
  const [user, setUser] = useState<CloudSecurityUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // API URL - API Gateway on port 8099
  const MAIN_API_URL = API_CONFIG.BASE_URL;

  // Get token from localStorage or parent window (if embedded in iframe)
  const getToken = useCallback((): string | null => {
    // First try localStorage
    let token = localStorage.getItem('access_token');
    if (token) {
      return token;
    }

    // If embedded in iframe, try to get token from sessionStorage (set by parent via postMessage)
    try {
      const isEmbedded = window.self !== window.top;
      if (isEmbedded) {
        token = sessionStorage.getItem('access_token');
        if (token) {
          // Also save to localStorage for future use
          localStorage.setItem('access_token', token);
          notifyAuthChanged();
          return token;
        }
      }
    } catch (e) {
    }

    return null;
  }, []);

  // Dev-only no-password auto-login. OFF by default — real credential login
  // (the login page → /auth/login) is the canonical path. Opt in for local
  // dev with REACT_APP_ENABLE_DEV_AUTO_LOGIN=true; the backend also requires
  // ALLOW_DEV_AUTH=true for the /auth/token bypass to respond.
  const autoDevLogin = useCallback(async (): Promise<string | null> => {
    const flag = process.env.REACT_APP_ENABLE_DEV_AUTO_LOGIN;
    const enabled = flag === 'true' || flag === '1';
    if (!enabled) {
      // No bypass — caller falls through to showing the login page.
      return null;
    }
    // Fallback: use /auth/token (no password required)
    try {
      const response = await fetch(`${MAIN_API_URL}/api/v1/auth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: 'admin-001',
          email: 'admin@cybercursor.com',
          roles: ['admin', 'analyst', 'viewer'],
          permissions: ['*'],
          tenant_id: 'default',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        notifyAuthChanged();
        localStorage.setItem('tenant_id', 'default');
        if (process.env.NODE_ENV !== 'production') {
          console.log('%c[Auth] Auto-login via /auth/token succeeded', 'color:#4ade80;font-weight:bold');
          console.log('[Auth] Roles:', data.roles);
        }
        return data.access_token;
      }
    } catch (e) {
      console.warn('[Auth] /auth/token fallback also failed:', e);
    }

    return null;
  }, [MAIN_API_URL]);

  // Fetch user profile from API
  const fetchUserProfile = useCallback(async () => {
    try {
      let token = getToken();
      if (!token) {
        token = await autoDevLogin();
        if (!token) {
          setLoading(false);
          return;
        }
      }

      setError(null);

      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Try to fetch from main API (shared auth) first
      let response = await fetch(`${MAIN_API_URL}/api/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const userData = await response.json();

        // Transform user data for cloud security context
        const cloudSecurityUser: CloudSecurityUser = {
          id: userData.user?.id || userData.id,
          email: userData.user?.email || userData.email,
          username: userData.user?.username || userData.username || userData.email?.split('@')[0],
          full_name: userData.user?.full_name || userData.full_name,
          role: userData.user?.role || userData.role || userData.user?.roles?.[0] || userData.roles?.[0] || 'user',
          is_active: userData.user?.is_active ?? userData.is_active ?? true,
          created_at: userData.user?.created_at || userData.created_at,
          modules: userData.modules || {
            cspm: ['read', 'write', 'scan', 'manage'],
            cwpp: ['read', 'write', 'monitor', 'manage'],
            ciem: ['read', 'write', 'analyze', 'manage'],
            dspm: ['read', 'write', 'classify', 'manage'],
            cdr: ['read', 'write', 'respond', 'manage'],
          },
          permissions_summary: userData.permissions_summary || {
            cspm: { read: true, write: true, scan: true, manage: true },
            cwpp: { read: true, write: true, monitor: true, manage: true },
            ciem: { read: true, write: true, analyze: true, manage: true },
            dspm: { read: true, write: true, classify: true, manage: true },
            cdr: { read: true, write: true, respond: true, manage: true },
          },
          scope: userData.scope || 'cspm:read,write,scan,manage cwpp:read,write,monitor,manage',
        };

        setUser(cloudSecurityUser);

        // Log auth context to browser console for visibility (dev only; never log the token)
        if (process.env.NODE_ENV !== 'production') {
          console.groupCollapsed('%c[Auth] Context initialized', 'color:#818cf8;font-weight:bold');
          console.log('User:', cloudSecurityUser.email, '|', cloudSecurityUser.role);
          console.log('Tenant:', localStorage.getItem('tenant_id') || 'default');
          console.log('Modules:', cloudSecurityUser.modules);
          console.log('Permissions:', cloudSecurityUser.permissions_summary);
          console.groupEnd();
        }

      } else if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('access_token');
        notifyAuthChanged();
        // Retry with auto-login instead of giving up
        const newToken = await autoDevLogin();
        if (newToken) {
          const retryRes = await fetch(`${MAIN_API_URL}/api/v1/auth/me`, {
            headers: { 'Authorization': `Bearer ${newToken}`, 'Content-Type': 'application/json' },
          });
          if (retryRes.ok) {
            const retryData = await retryRes.json();
            setUser({
              id: retryData.user?.id || retryData.id,
              email: retryData.user?.email || retryData.email,
              username: retryData.user?.username || retryData.username || retryData.email?.split('@')[0],
              full_name: retryData.user?.full_name || retryData.full_name,
              role: retryData.user?.role || retryData.role || retryData.user?.roles?.[0] || retryData.roles?.[0] || 'user',
              is_active: retryData.user?.is_active ?? retryData.is_active ?? true,
              created_at: retryData.user?.created_at || retryData.created_at,
              modules: retryData.modules || {
                cspm: ['read', 'write', 'scan', 'manage'], cwpp: ['read', 'write', 'monitor', 'manage'],
                ciem: ['read', 'write', 'analyze', 'manage'], dspm: ['read', 'write', 'classify', 'manage'],
                cdr: ['read', 'write', 'respond', 'manage'],
              },
              permissions_summary: retryData.permissions_summary || {
                cspm: { read: true, write: true, scan: true, manage: true },
                cwpp: { read: true, write: true, monitor: true, manage: true },
                ciem: { read: true, write: true, analyze: true, manage: true },
                dspm: { read: true, write: true, classify: true, manage: true },
                cdr: { read: true, write: true, respond: true, manage: true },
              },
              scope: retryData.scope || 'cspm:read,write,scan,manage cwpp:read,write,monitor,manage',
            });
          } else {
            setUser(null);
            setError('Session expired. Please log in again.');
          }
        } else {
          setUser(null);
          setError('Session expired. Please log in again.');
        }
      } else {
        console.warn('CloudSecurityAuthContext - unexpected response:', response.status);
        setError(`Authentication error: ${response.status}`);
      }
    } catch (err) {
      console.error('CloudSecurityAuthContext - error fetching user profile:', err);
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Request timed out');
      } else {
        setError('Failed to authenticate');
      }
      // Don't clear token on network errors - might be temporary
    } finally {
      setLoading(false);
    }
  }, [getToken, autoDevLogin, MAIN_API_URL]);

  // Check authentication on mount
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Listen for token from parent window (when embedded in iframe)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle token sent from parent
      if (event.data?.type === 'AUTH_TOKEN' && event.data?.token) {
        localStorage.setItem('access_token', event.data.token);
        sessionStorage.setItem('access_token', event.data.token);
        notifyAuthChanged();
        // Refetch user profile with the new token
        fetchUserProfile();
      }
    };

    window.addEventListener('message', handleMessage);

    // If embedded in iframe, request token from parent
    const isEmbedded = window.self !== window.top;
    if (isEmbedded && window.parent) {
      window.parent.postMessage({ type: 'REQUEST_AUTH_TOKEN' }, '*');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [fetchUserProfile]);

  // Global session-expiry handler. apiClient broadcasts `auth:session-expired`
  // (debounced) the first time any authenticated request returns 401 — the
  // token has already been cleared there. Re-run the bootstrap: in dev this
  // re-mints via auto-login (if enabled); in prod there's no token left, so
  // the app falls back to the login page instead of leaving every panel in a
  // scattered error state.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      setError('Session expired. Please log in again.');
      fetchUserProfile();
    };
    window.addEventListener('auth:session-expired', onExpired);
    return () => window.removeEventListener('auth:session-expired', onExpired);
  }, [fetchUserProfile]);

  // Login function
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${MAIN_API_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      notifyAuthChanged();

      // Fetch user profile after login
      await fetchUserProfile();
    } catch (err) {
      console.error('CloudSecurityAuthContext - login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('access_token');
    notifyAuthChanged();
    setUser(null);
    setError(null);
    // Optionally redirect to login
    if (window.parent !== window) {
      // If in iframe, notify parent
      window.parent.postMessage({ type: 'LOGOUT' }, '*');
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    await fetchUserProfile();
  };

  // Get auth token
  const getAuthToken = () => getToken();

  // Check if user has permission for a specific action
  const hasPermission = (module: string, action: string): boolean => {
    if (!user) return false;

    // Admin has all permissions
    if (user.role === 'admin') return true;

    // Check permissions_summary
    const modulePerms = user.permissions_summary?.[module as keyof typeof user.permissions_summary];
    if (modulePerms && modulePerms[action as keyof typeof modulePerms]) {
      return true;
    }

    // Check modules array
    const moduleActions = user.modules?.[module as keyof typeof user.modules];
    if (moduleActions?.includes(action)) {
      return true;
    }

    // Check scope string
    if (user.scope?.includes(`${module}:${action}`)) {
      return true;
    }

    return false;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    error,
    login,
    logout,
    refreshUser,
    getAuthToken,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected route component
interface ProtectedRouteProps {
  children: ReactNode;
  requiredModule?: string;
  requiredAction?: string;
  fallback?: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredModule,
  requiredAction,
  fallback,
}) => {
  const { isAuthenticated, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback ? <>{fallback}</> : (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <p>Please log in to access this content.</p>
      </div>
    );
  }

  if (requiredModule && requiredAction && !hasPermission(requiredModule, requiredAction)) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <p>You do not have permission to access this feature.</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthContext;
